import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import {
  DataDisputeByIdDocument,
  DataDisputeItemInfoFragment,
  DataDisputeItemStatus,
  InboxItemsScrollableListDocument,
  useDataDisputeItemChangeStatusMutation,
} from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useIsControlledByPeoOrPeoPayrollProvider } from 'src/hooks/employer';
import {
  useCurrentInboxItemDataDispute,
  useInboxItemParamId,
  useInboxItemUnreadStatuses,
} from 'src/hooks/inboxItem';
import { useModalV2 } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions, useCurrentUserStaff } from 'src/hooks/user';
import { EntityActionConfirmationModal } from 'src/modals/common';

import { DataDisputeItemInfo, NavigateToEmployeeButton, VerificationRequestLink } from './shared';
import { DataDisputeVRConnectionStatus } from './types';
import { checkDataDisputeVerificationRequestStatus, getDataDisputeEmployerFilter } from './utils';

export const InboxItemContentDataDisputeEmployer: React.FC = () => {
  const { openModal } = useModalV2();
  const { createApolloContext } = useNotification();

  const { user } = useCurrentUser();
  const { employerStaff, peoStaff, peoPayrollProviderStaff } = useCurrentUserStaff();
  const isControlledByPeoOrPeoPayrollProvider = useIsControlledByPeoOrPeoPayrollProvider();
  const userPermissions = useCurrentUserPermissions();

  const inboxItemId = useInboxItemParamId();
  const { updateInboxItemUnreadStatuses } = useInboxItemUnreadStatuses(inboxItemId);

  const { dataDispute, loading } = useCurrentInboxItemDataDispute(
    getDataDisputeEmployerFilter(
      user,
      employerStaff?.employer,
      peoStaff?.peo,
      peoPayrollProviderStaff?.peoPayrollProvider,
      userPermissions,
    ),
  );

  const [dataDisputeItemChangeStatusMutation] = useDataDisputeItemChangeStatusMutation({
    context: createApolloContext({ successMessage: 'Data Dispute has been successfully resolved' }),
    refetchQueries: [InboxItemsScrollableListDocument, DataDisputeByIdDocument],
  });

  const handleResolve = React.useCallback(
    (dataDisputeItem: DataDisputeItemInfoFragment) => {
      openModal(EntityActionConfirmationModal, {
        id: ModalId.EntityActionConfirmation,
        action: 'resolve',
        entity: 'this Data Dispute item',
        onSubmit: async () => {
          if (!dataDisputeItem.id) {
            return;
          }
          await Promise.all([
            dataDisputeItemChangeStatusMutation({
              variables: {
                dataDisputeItemId: dataDisputeItem.id,
                status: DataDisputeItemStatus.Resolved,
              },
            }),
            updateInboxItemUnreadStatuses(),
          ]);
        },
      });
    },
    [dataDisputeItemChangeStatusMutation, openModal, updateInboxItemUnreadStatuses],
  );

  const dataDisputeVerificationRequestStatus =
    checkDataDisputeVerificationRequestStatus(dataDispute);

  // The employee has no linked user until they sign up, so fall back to a noun
  // rather than opening the sentence with a blank.
  const employeeName = dataDispute?.employee.user?.fullName ?? 'The Employee';

  // PEO and payroll-provider staff resolve on an employer's behalf, not their own.
  const resolvesOnBehalfOfEmployer =
    userPermissions.hasPeoRoles || userPermissions.isPeoPayrollProviderUser;

  const subtitleText = React.useMemo((): React.ReactElement => {
    if (
      dataDisputeVerificationRequestStatus === DataDisputeVRConnectionStatus.pendingWithConnect ||
      dataDisputeVerificationRequestStatus === DataDisputeVRConnectionStatus.resolvedWithConnect
    ) {
      return (
        <React.Fragment>
          {employeeName} opened a Data Dispute based on&nbsp;
          <VerificationRequestLink verificationRequest={dataDispute?.verificationRequest} />.
          {isControlledByPeoOrPeoPayrollProvider &&
            ' This Data Dispute will be managed by your PEO.'}
          {dataDispute?.reason && (
            <Typography mt={2} color="error">
              Employee Comment: {dataDispute.reason}
            </Typography>
          )}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        {employeeName} opened a Data Dispute. Please check the data associated with this Employee in
        your payroll provider system.
        {isControlledByPeoOrPeoPayrollProvider && ' This Data Dispute will be managed by your PEO.'}
        {dataDispute?.reason && (
          <Typography mt={2} color="error">
            Employee Comment: {dataDispute.reason}
          </Typography>
        )}
      </React.Fragment>
    );
  }, [
    employeeName,
    dataDispute?.reason,
    dataDispute?.verificationRequest,
    dataDisputeVerificationRequestStatus,
    isControlledByPeoOrPeoPayrollProvider,
  ]);
  return (
    <Paper style={{ borderRadius: 8 }} variant="outlined" sx={{ p: 4 }}>
      {loading ? (
        <FullscreenLoader />
      ) : (
        <React.Fragment>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Typography variant="h6">
              {resolvesOnBehalfOfEmployer ? 'Employer Resolution Status' : 'Your Resolution Status'}
            </Typography>
            <NavigateToEmployeeButton employeeId={dataDispute?.employee.id} />
          </Box>

          <Typography variant="body1" sx={theme => ({ color: theme.palette.grey['700'] })} mt={1}>
            {subtitleText}
          </Typography>

          <Box mt={3} display="flex" flexDirection="column" gap={3}>
            {dataDispute?.dataDisputeItemsList.map(dataDisputeInfo => (
              <DataDisputeItemInfo
                key={dataDisputeInfo.id}
                dataDisputeItem={dataDisputeInfo}
                onResolve={handleResolve}
              />
            ))}
          </Box>
        </React.Fragment>
      )}
    </Paper>
  );
};
